import { useCallback, useEffect, useState } from "react";
import { isEmpty, sortBy } from "lodash";
import { useApi } from "@services/useApi";
import { LBtn, LCheckbox, LModal } from "@components";
import { LSelect, SelectOption } from "@components/LSelect";
import { convertUTCToLocalTimeZone } from "@helpers/dateHelpers";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { Loader } from "@components/Loader";
import { LanguageId } from "@store/generalTypes";
import { parseHistoryStatus } from "@helpers/enumHelper";
import { ExamineeContactInfo } from "../../../../store/newExamineeSlice";
import { useModal } from "@services/ModalContext";
import { StartAssessmentStepNames, setStartAssessmentContext } from "../../../../store/startAssessmentSlice";
import { setAssessmentForms } from "../../../../store/assessmentContextSlice";
import { boolean, number, object, SchemaOf, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { useNavigate } from "react-router";
import { NavItems } from "@helpers/navigation";
import { translate } from "react-i18nify";
import { useAssessmentHistory } from "../../../../useAssessmentHistory";
import "../../../../assessmentHistory.css";

interface StartAssessmentForm {
    assessmentFormId: number;
    isRetest?: boolean;
}

const formSchema: SchemaOf<StartAssessmentForm> = object({
    assessmentFormId: number().required(),
    isRetest: boolean().optional()
});

export const ChooseAssessmentForm = ({
    examinee,
    locationId,
    upcomingAssessmentId,
    onEditExamineeEmail
}: ChooseAssessmentFormProps): JSX.Element => {
    const assessmentContext = useAppSelector((x) => x.assessmentContext);

    const [isLoading, setIsLoading] = useState(true);
    const [assessmentFormOptions, setAssessmentFormOptions] = useState<SelectOption[]>([]);
    const { history: assessmentHistory, error: historyError } = useAssessmentHistory(locationId, examinee.id);
    const [message, setMessage] = useState("");
    const [closeAfterMessage, setCloseAfterMessage] = useState(false);
    const [isMissingEmailMessage, setIsMissingEmailMessage] = useState(false);
    const [isSendingLink, setIsSendingLink] = useState(false);

    const dispatch = useAppDispatch();
    const validateMethods = useForm<StartAssessmentForm>({
        resolver: yupResolver(formSchema)
    });
    const { closeModal, setFormName } = useModal();
    const { getAssessmentForms, sendAssessmentLink } = useApi();
    const navigate = useNavigate();

    useEffect(() => {
        setFormName?.(StartAssessmentStepNames.AssessmentForm);
        void loadForm();
    }, []);

    const loadForm = useCallback(async () => {
        await loadAssessmentForms();

        setIsLoading(false);
    }, []);

    const loadAssessmentForms = async (): Promise<void> => {
        let forms = assessmentContext.assessmentForms;

        if (isEmpty(forms)) {
            forms = await getAssessmentForms();
            dispatch(setAssessmentForms(forms));
        }

        const options = forms.map((af) => ({
            name: af.name,
            value: af.id.toString()
        }));

        setAssessmentFormOptions(sortBy(options, "name"));
    };

    const handleOnSubmit = (data: StartAssessmentForm): void => {
        dispatch(
            setStartAssessmentContext({
                ...data,
                examineeId: examinee.id,
                locationId,
                upcomingAssessmentId
            })
        );

        navigate(NavItems.Assessments.Landing.path);
    };

    const hasValidEmail = (): boolean => {
        return string().email().required().isValidSync(examinee.email?.trim());
    };

    const handleSendAssessmentLink = async (data: StartAssessmentForm): Promise<void> => {
        if (!hasValidEmail()) {
            setMessage(translate("admin.assessmentForm.invalidEmailMessage"));
            setCloseAfterMessage(false);
            setIsMissingEmailMessage(true);
            return;
        }

        setIsMissingEmailMessage(false);
        setIsSendingLink(true);

        try {
            await sendAssessmentLink(
                locationId,
                examinee.id,
                LanguageId.en,
                data.assessmentFormId,
                upcomingAssessmentId,
                data.isRetest
            );
            setMessage(translate("admin.assessmentForm.linkSentMessage"));
            setCloseAfterMessage(true);
        } catch {
            setMessage(translate("admin.assessmentForm.linkErrorMessage"));
            setCloseAfterMessage(false);
            setIsMissingEmailMessage(false);
        } finally {
            setIsSendingLink(false);
        }
    };

    const handleMessageClose = (): void => {
        setMessage("");
        setIsMissingEmailMessage(false);

        if (closeAfterMessage) {
            closeModal();
        }
    };

    const handleMissingEmailCancel = (): void => {
        setMessage("");
        setIsMissingEmailMessage(false);
    };

    const handleMissingEmailEdit = (): void => {
        setMessage("");
        setIsMissingEmailMessage(false);
        onEditExamineeEmail?.();
    };

    const handleSendClick = (): void => {
        void validateMethods.handleSubmit(handleSendAssessmentLink)();
    };

    const renderForm = (): JSX.Element => {
        return (
            <FormProvider {...validateMethods}>
                <form
                    id={StartAssessmentStepNames.AssessmentForm}
                    onSubmit={validateMethods.handleSubmit(handleOnSubmit)}
                >
                    <div className="mt-5">
                        <LSelect
                            options={assessmentFormOptions}
                            label="Select Version"
                            inputName="assessmentFormId"
                            defaultValue={assessmentFormOptions[0].name}
                            isFullWidth
                            tooltip="We recommend testing Form A first, followed by Form B, then Form C. Repeat as needed."
                        />
                    </div>

                    <div className="is-flex is-flex-direction-row is-justify-content-space-between">
                        <div>This is a retest</div>
                        <LCheckbox inputName="isRetest" />
                    </div>

                    <div className="fatDivider my-5" />

                    {historyError && (
                        <p role="alert">Unable to load assessment history. Please reopen this view to retry.</p>
                    )}
                    {!isEmpty(assessmentHistory) && (
                        <div>
                            <p className="has-text-weight-bold mb-4">Test Version History</p>
                            {assessmentHistory.map((h, index) => {
                                return (
                                    <div key={`${h.id}-${index}`} className="assessment-version-history">
                                        <p className="column p-0 is-3">{h.assessmentFormName}</p>
                                        <p className="column p-0">
                                            {format(convertUTCToLocalTimeZone(h.launchedDate), "PP")}
                                        </p>
                                        <p className="column p-0 assessment-history-status">
                                            {parseHistoryStatus(h).replace("Pre-", "Pre\u2011")}
                                        </p>
                                        <p className="column p-0">
                                            {h.assessmentType === "InPerson" ? "In-person" : h.assessmentType}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center mt-6">
                        <LBtn variation="unstyled" onClick={closeModal} isBold noSpacing>
                            {translate("admin.assessmentForm.back")}
                        </LBtn>
                        <div className="is-flex is-flex-direction-row is-align-items-center">
                            <LBtn
                                variation="outline"
                                onClick={handleSendClick}
                                loading={isSendingLink}
                                isBold
                                noSpacing
                            >
                                {translate("admin.assessmentForm.sendAssessmentLink")}
                            </LBtn>
                            <LBtn type="submit" isBold>
                                {translate("admin.assessmentForm.startAssessment")}
                            </LBtn>
                        </div>
                    </div>
                </form>
            </FormProvider>
        );
    };

    return (
        <>
            <div className="container has-text-centered">
                <div className="mw-60 has-text-left">{isLoading ? <Loader /> : renderForm()}</div>
            </div>
            {message && (
                <LModal
                    onClose={handleMessageClose}
                    footer={
                        <div className="is-flex is-justify-content-center">
                            {isMissingEmailMessage ? (
                                <>
                                    <LBtn variation="unstyled" onClick={handleMissingEmailCancel} isBold>
                                        {translate("admin.cancel")}
                                    </LBtn>
                                    {onEditExamineeEmail && (
                                        <LBtn onClick={handleMissingEmailEdit} isBold>
                                            {translate("admin.assessmentForm.enterEmailAddress")}
                                        </LBtn>
                                    )}
                                </>
                            ) : (
                                <LBtn onClick={handleMessageClose} isBold>
                                    OK
                                </LBtn>
                            )}
                        </div>
                    }
                    width="s"
                    fullHeight={false}
                >
                    <p className="has-text-centered">{message}</p>
                </LModal>
            )}
        </>
    );
};

interface ChooseAssessmentFormProps {
    examinee: ExamineeContactInfo;
    locationId: number;
    upcomingAssessmentId?: number;
    onEditExamineeEmail?: () => void;
}
